// waap-tab.tsx — WAAP vendor on-demand tab for the Operations service detail page.
// Shows the active subscription package (with Refresh) and a vendor↔store diff
// table for WAAP websites. Only rendered when service.provider.category === 'waap'.
import { useState } from 'react';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardDescription, CardHeader, CardTitle, Skeleton, Tooltip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useOpsWAAPPackage,
  useOpsWAAPWebsites,
  type OpsWAAPWebsiteRow,
  type WaapDomainStatus,
} from '@/api/operations';

/* ── Diff badge ──────────────────────────────────────────────────────────── */

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

const STATUS_TONE: Record<WaapDomainStatus, BadgeTone> = {
  matched: 'success',
  store_only: 'warning',
  vendor_only: 'info',
};

const STATUS_LABEL: Record<WaapDomainStatus, string> = {
  matched: 'Matched',
  store_only: 'Store only',
  vendor_only: 'Vendor only',
};

const STATUS_TOOLTIP: Record<WaapDomainStatus, string> = {
  matched: 'Domain exists in both vendor and store bindings.',
  store_only: 'Domain is in the store but not found at vendor — may be deleted or not yet provisioned.',
  vendor_only: 'Domain exists at vendor but has no store binding — possible orphan or pending onboarding.',
};

function DiffBadge({ row }: { row: OpsWAAPWebsiteRow }) {
  const tone = row.mismatch ? 'danger' : STATUS_TONE[row.source_status];
  const label = row.mismatch ? 'ID mismatch' : STATUS_LABEL[row.source_status];
  const tip = row.mismatch
    ? `Store website_id ${row.store_website_id} ≠ vendor id ${row.vendor_website_id}`
    : STATUS_TOOLTIP[row.source_status];

  return (
    <Tooltip content={tip}>
      <Badge tone={tone}>{label}</Badge>
    </Tooltip>
  );
}

/* ── Column definitions ──────────────────────────────────────────────────── */

const websiteColumns: ColumnDef<OpsWAAPWebsiteRow, unknown>[] = [
  {
    id: 'domain',
    accessorKey: 'domain',
    header: 'Domain',
    cell: ({ row }) => (
      <span style={{ fontWeight: 500, color: 'var(--vn-fg-default)' }}>
        {row.original.domain}
      </span>
    ),
  },
  {
    id: 'source_status',
    header: 'Diff',
    cell: ({ row }) => <DiffBadge row={row.original} />,
  },
  {
    id: 'vendor_website_id',
    header: 'Vendor website ID',
    cell: ({ row }) =>
      row.original.vendor_website_id ? (
        <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {row.original.vendor_website_id}
        </span>
      ) : (
        <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
      ),
  },
  {
    id: 'store_website_id',
    header: 'Store website ID',
    cell: ({ row }) =>
      row.original.store_website_id ? (
        <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {row.original.store_website_id}
        </span>
      ) : (
        <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
      ),
  },
  {
    id: 'store_binding_status',
    header: 'Store status',
    cell: ({ row }) =>
      row.original.store_binding_status ? (
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {row.original.store_binding_status}
        </span>
      ) : (
        <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
      ),
  },
];

/* ── Package card ────────────────────────────────────────────────────────── */

interface PackageCardProps {
  serviceId: string;
}

function PackageCard({ serviceId }: PackageCardProps) {
  const [refresh, setRefresh] = useState(false);
  const [enabled, setEnabled] = useState(false);

  const pkg = useOpsWAAPPackage(serviceId, { enabled, refresh });

  const handleLoad = () => {
    setRefresh(false);
    setEnabled(true);
  };

  const handleRefresh = () => {
    setRefresh(true);
    setEnabled(true);
    // Reset refresh flag after triggering so next manual refresh works.
    setTimeout(() => setRefresh(false), 200);
  };

  return (
    <Card>
      <CardHeader
        actions={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
            {!enabled && (
              <Button size="sm" variant="secondary" onClick={handleLoad}>
                Load
              </Button>
            )}
            {enabled && (
              <Tooltip content="Force a fresh vendor call, bypassing the 8h cache">
                <Button
                  size="sm"
                  variant="ghost"
                  startIcon={<RefreshCw size={14} />}
                  onClick={handleRefresh}
                  disabled={pkg.isFetching}
                >
                  {pkg.isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
              </Tooltip>
            )}
          </div>
        }
      >
        <CardTitle>Subscription Package</CardTitle>
      </CardHeader>
      <CardBody>
        {!enabled && (
          <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
            Click Load to fetch the vendor package — not auto-loaded to avoid login storms.
          </span>
        )}
        {enabled && pkg.isLoading && (
          <div style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'center' }}>
            <Skeleton style={{ width: 120, height: 20 }} />
            <Skeleton style={{ width: 80, height: 20 }} />
          </div>
        )}
        {enabled && pkg.isError && (
          <Alert
            tone="danger"
            title="Failed to load package"
            actions={
              <Button size="sm" variant="ghost" onClick={() => pkg.refetch()}>
                Retry
              </Button>
            }
          />
        )}
        {enabled && pkg.data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
              <span style={{ fontSize: 'var(--vn-font-size-lg)', fontWeight: 600, color: 'var(--vn-fg-default)' }}>
                {pkg.data.package_name || pkg.data.package_id || 'No package'}
              </span>
              {pkg.data.package_id && (
                <Badge tone="neutral">{pkg.data.package_id}</Badge>
              )}
              {pkg.data.cached && (
                <Tooltip content={`Cached at ${pkg.data.fetched_at}`}>
                  <Badge tone="neutral">cached</Badge>
                </Tooltip>
              )}
            </div>
            {pkg.data.entity_id && (
              <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)', fontFamily: 'monospace' }}>
                Entity: {pkg.data.entity_id}
              </span>
            )}
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
              Fetched: {pkg.data.fetched_at}
            </span>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/* ── Websites table ──────────────────────────────────────────────────────── */

interface WebsitesCardProps {
  serviceId: string;
}

function WebsitesCard({ serviceId }: WebsitesCardProps) {
  const [enabled, setEnabled] = useState(false);
  const websites = useOpsWAAPWebsites(serviceId, { enabled });

  if (!enabled) {
    return (
      <Card>
        <CardHeader><CardTitle>Websites (vendor↔store diff)</CardTitle></CardHeader>
        <CardBody>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--vn-space-3)' }}>
            <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>
              Click Load to fetch live vendor websites and compare with store bindings.
            </span>
            <Button size="sm" variant="secondary" onClick={() => setEnabled(true)}>
              Load
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (websites.isError) {
    return (
      <Card>
        <CardHeader><CardTitle>Websites (vendor↔store diff)</CardTitle></CardHeader>
        <CardBody>
          <Alert
            tone="danger"
            title="Failed to load vendor websites"
            actions={
              <Button size="sm" variant="ghost" onClick={() => websites.refetch()}>
                Retry
              </Button>
            }
          />
        </CardBody>
      </Card>
    );
  }

  if (websites.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>Websites (vendor↔store diff)</CardTitle></CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  const diff = websites.data;
  const rows = diff?.domains ?? [];
  const mismatchCount = rows.filter((r) => r.mismatch).length;
  const vendorOnlyCount = rows.filter((r) => r.source_status === 'vendor_only').length;
  const storeOnlyCount = rows.filter((r) => r.source_status === 'store_only').length;

  const diffSummary = diff
    ? `Vendor: ${diff.vendor_count} · Store: ${diff.store_count}${mismatchCount > 0 ? ` · ${mismatchCount} ID mismatch` : ''}${vendorOnlyCount > 0 ? ` · ${vendorOnlyCount} vendor-only` : ''}${storeOnlyCount > 0 ? ` · ${storeOnlyCount} store-only` : ''}`
    : undefined;

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            size="sm"
            variant="ghost"
            startIcon={<RefreshCw size={14} />}
            onClick={() => websites.refetch()}
            disabled={websites.isFetching}
          >
            {websites.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      >
        <CardTitle>Websites (vendor&#8596;store diff)</CardTitle>
        {diffSummary && <CardDescription>{diffSummary}</CardDescription>}
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={websiteColumns}
          data={rows}
          getRowId={(r) => r.domain}
          dense
          emptyState={
            <EmptyState
              icon={<ShieldCheck size={24} />}
              title="No websites"
              description="Vendor returned an empty website list."
            />
          }
          footer={
            rows.length > 0
              ? `${rows.length} domain${rows.length !== 1 ? 's' : ''} · fetched ${diff?.fetched_at ?? ''}`
              : undefined
          }
        />
      </CardBody>
    </Card>
  );
}

/* ── Tab root ────────────────────────────────────────────────────────────── */

interface WaapTabProps {
  serviceId: string;
}

/**
 * WaapTab renders the WAAP vendor on-demand panel.
 * Both sections are load-on-demand (not auto-fetched) to avoid vendor login storms.
 * Lazy-mounted by the parent — only rendered when tab === 'waap'.
 */
export function WaapTab({ serviceId }: WaapTabProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-4)' }}>
      <PackageCard serviceId={serviceId} />
      <WebsitesCard serviceId={serviceId} />
    </div>
  );
}
